/**
 * jobSearch.js — What the Jobs search box actually looks at.
 *
 * The old search checked five fields with one `includes()` of the whole query,
 * so "smith oak" found nothing (no single field holds both words) and a phone
 * number found nothing at all. This flattens each job into one haystack and
 * requires EVERY token to appear somewhere in it — order-free AND matching
 * across fields.
 *
 * Covered: title, client, contact, location, type, status (raw + label),
 * phone (raw and digits-only), email, notes, quote notes, handoff notes,
 * deliverables, delivery links, void/discount reasons, crew names from the
 * calc snapshot and assigned_crew, originator, referrer, property type,
 * beds/baths/sqft, shoot and delivery dates (ISO and written), and the raw
 * job / project / quote ids.
 */
import { formatStatus } from './utils';
import { getAllPeople } from './calcEngine';

/** Pull every string out of an arbitrary JSON value (handoff notes, crew arrays). */
function collectStrings(value, out, depth = 0) {
  if (value == null || depth > 4) return;
  if (typeof value === 'string') {
    if (value.length && value.length < 400) out.push(value);
    return;
  }
  if (typeof value === 'number') { out.push(String(value)); return; }
  if (Array.isArray(value)) {
    for (const v of value) collectStrings(v, out, depth + 1);
    return;
  }
  if (typeof value === 'object') {
    for (const v of Object.values(value)) collectStrings(v, out, depth + 1);
  }
}

/** ISO day plus the ways a person would actually type that date. */
function pushDate(out, v) {
  if (!v) return;
  const iso = String(v).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
  out.push(iso);
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return;
  out.push(dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
  out.push(dt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
}

/** Everything about one job, lowercased into a single searchable string. */
export function buildHaystack(job) {
  if (!job) return '';
  const parts = [];

  parts.push(
    job.title, job.clientName, job.contactName, job.location, job.jobType,
    job.contactEmail, job.originator, job.referredBy,
    job.propertyType, job.bedrooms, job.bathrooms, job.sqft,
    job.notes, job.quoteNotes, job.deliverables, job.deliveryLinks,
    job.discountReason, job.voidReason,
    job.id, job.projectId, job.quoteId, job.source, job.workflowType,
  );

  // Status matches on the stored value and the label the UI shows, so both
  // "post_production" and "post production" work.
  if (job.status) parts.push(job.status, formatStatus(job.status));

  // Phone twice: as typed, and digits-only so any punctuation style hits.
  if (job.contactPhone) {
    parts.push(job.contactPhone, String(job.contactPhone).replace(/\D/g, ''));
  }

  // Crew: whoever the saved split names, plus the hand-assigned list.
  try {
    for (const p of getAllPeople(job.calcSnapshot)) if (p?.name) parts.push(p.name);
  } catch { /* a malformed snapshot must never break search */ }
  collectStrings(job.assignedCrew, parts);
  collectStrings(job.handoffNotes, parts);

  pushDate(parts, job.shootDate);
  pushDate(parts, job.deliveryDate);
  pushDate(parts, job.createdAt);
  if (Array.isArray(job.shootDates)) for (const d of job.shootDates) pushDate(parts, d);

  return parts.filter(Boolean).join(' ').toLowerCase();
}

// Jobs are replaced wholesale by react-query on every refetch, so keying the
// cache on the object identity both hits on re-renders and self-evicts.
const haystackCache = new WeakMap();

export function haystackFor(job) {
  if (!job || typeof job !== 'object') return '';
  let h = haystackCache.get(job);
  if (h === undefined) {
    h = buildHaystack(job);
    haystackCache.set(job, h);
  }
  return h;
}

/** Split a query into lowercase tokens. Quoted "phrases like this" stay whole. */
export function tokenizeQuery(query) {
  const q = String(query || '').toLowerCase().trim();
  if (!q) return [];
  const tokens = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m;
  while ((m = re.exec(q)) !== null) {
    const t = (m[1] ?? m[2]).trim();
    if (t) tokens.push(t);
  }
  return tokens;
}

/** Every token must appear somewhere in the haystack. Order does not matter. */
export function matchesTokens(job, tokens) {
  if (!tokens.length) return true;
  const hay = haystackFor(job);
  if (!hay) return false;
  return tokens.every((t) => {
    if (hay.includes(t)) return true;
    // "805-555" / "(805) 555" should still find 8055551234.
    if (/\d/.test(t)) {
      const digits = t.replace(/\D/g, '');
      if (digits.length >= 3 && hay.includes(digits)) return true;
    }
    return false;
  });
}
